import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import T from '../i18n/content.js';
import { useLang, useT } from '../i18n/LangContext.jsx';
import { api } from '../api/client.js';
import MediaCarousel from './MediaCarousel.jsx';

const NOTES_PER_PAGE = 5;

export default function InsightsFeed() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState('notes');
  const [mediaIndex, setMediaIndex] = useState(0);
  const [notes, setNotes] = useState([]);
  const [media, setMedia] = useState([]);
  const [notesPage, setNotesPage] = useState(1);
  const [totalNotesPages, setTotalNotesPages] = useState(1);

  const S = T.insightsSec;
  const featured = media[mediaIndex];

  // The Long-form & Media tab is shown only when the backend actually has media
  // for the current language; with none it stays hidden, leaving just Notes.
  // Uploading media (admin) makes the tab reappear on its own — no code change.
  const hasMedia = media.length > 0;
  const TABS = hasMedia ? ['notes', 'media'] : ['notes'];

  // Switching language switches to the other (independent) content set.
  useEffect(() => {
    setNotesPage(1);
    setMediaIndex(0);
  }, [lang]);

  useEffect(() => {
    api.insights({ lang, page: notesPage, limit: NOTES_PER_PAGE })
      .then(r => {
        setNotes(r.data);
        setTotalNotesPages(r.meta.total_pages || 1);
      })
      .catch(() => {});
  }, [notesPage, lang]);

  useEffect(() => {
    api.mediums({ lang }).then(setMedia).catch(() => {});
  }, [lang]);

  // If media drops to zero (a language with none, or all media removed), don't
  // strand the user on the now-hidden Media tab.
  useEffect(() => { if (media.length === 0) setTab('notes'); }, [media]);

  return (
    <section className="section feed">
      {/* 两个 tab 共用同一外壳；不再因 media 切换而改形（保持左块尺寸/网格/宽度一致） */}
      <div className="wrap feed__wrap">
        <aside className="feed__title-block">
          {lang === 'en' ? (
            <>
              <div className="feed__t2">News<br />and<br />Insights</div>
              <div className="feed__t3">
                Information is abundant.<br />Judgment is not.
              </div>
            </>
          ) : (
            <>
              <div className="feed__t2 cn">资 讯<br />与<br />洞 见</div>
              <div className="feed__t3 cn">
                信 息 从 不 稀 缺<br />判 断 方 为 难 得
              </div>
            </>
          )}
        </aside>

        <span className="feed__rule" aria-hidden="true" />

        <div className="feed__content">
          <div className="feed__tabs" role="tablist">
            {TABS.map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                className={`feed__tab ${tab === key ? 'is-active' : ''}`}
                onClick={() => setTab(key)}
              >
                {t(S.tabs[key])}
              </button>
            ))}
          </div>

          {tab === 'notes' && (
            <>
              <div className="feed__notes">
                {notes.map((n) => (
                  <a className="note-row"
                     href={`/insights/${n.id}`}
                     key={n.id}
                     onClick={e => { e.preventDefault(); navigate(`/insights/${n.id}`); }}>
                    <div className="note-row__meta">
                      {/* series/category (n.cat) temporarily hidden on the frontend */}
                      <span>{n.date}</span>
                    </div>
                    <div className="note-row__title">{n.title}</div>
                    <div className="note-row__excerpt">{n.excerpt}</div>
                  </a>
                ))}
                {notes.length === 0 && (
                  <p className="feed__media-empty">{lang === 'en' ? 'No articles yet.' : '暂 无 文 章。'}</p>
                )}
              </div>

              {totalNotesPages > 1 && (
                <nav className="feed__pager" aria-label={lang === 'en' ? 'Notes pagination' : '短 札 分 页'}>
                  <button
                    type="button"
                    className="pager__nav"
                    disabled={notesPage === 1}
                    onClick={() => setNotesPage(notesPage - 1)}
                  >
                    {lang === 'en' ? 'Previous' : '上 一 页'}
                  </button>
                  <ol className="pager__pages">
                    {Array.from({ length: totalNotesPages }, (_, i) => i + 1).map((n) => (
                      <li key={n}>
                        <button
                          type="button"
                          className={`pager__num ${n === notesPage ? 'is-active' : ''}`}
                          aria-current={n === notesPage ? 'page' : undefined}
                          onClick={() => setNotesPage(n)}
                        >
                          {n}
                        </button>
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    className="pager__nav"
                    disabled={notesPage === totalNotesPages}
                    onClick={() => setNotesPage(notesPage + 1)}
                  >
                    {lang === 'en' ? 'Next' : '下 一 页'}
                  </button>
                </nav>
              )}
            </>
          )}

          {tab === 'media' && (
            <div className="feed__media">
              {media.length === 0 ? (
                <p className="feed__media-empty">{t(S.mediaEmpty)}</p>
              ) : (
                <>
                  <header className="feed__media-head">
                    <h3 className="feed__media-title">{featured.title}</h3>
                    <div className="feed__media-date">{featured.date}</div>
                    <p className="feed__media-intro">{featured.intro}</p>
                  </header>
                  <MediaCarousel items={media} onSlideChange={setMediaIndex} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
